import "aframe";
import React, { ReactNode, RefObject } from "react";
import CameraPositions from "./CameraPositions";
import { ObjectOverlays } from "./ObjectOverlays";
import { AFrameCamera, AFrameScene, AFrameSky, CameraPosition } from "./types";

import doneIcon from "assets/images/check.png";
import cancelIcon from "assets/images/close.png";
import "./utils/lookControls";

interface PanoramaSceneProps {
  sceneRef: RefObject<AFrameScene | null>;
  cameraRef: RefObject<AFrameCamera | null>;
  skyRef: RefObject<AFrameSky | null>;
  currentSrc: string | null;
  isSplitView: boolean;
  FOV: number;
  cameraPositions: CameraPosition[];
  loadedImageIndex: number | false;
  debugCameraPositions: boolean;
  transformCameraPosition: (
    position: [number, number, number],
    currentCameraPosition: [number, number, number],
    scaleFactor?: number,
    offset?: [number, number, number],
  ) => [number, number, number];
  activeObjectId?: string | null;
  children?: ReactNode;
  isSecondScene?: boolean;
  imageWidth: number;
  imageHeight: number;
  onSelectObject?: (objectId: string) => void;
}

const PanoramaScene = ({
  sceneRef,
  cameraRef,
  skyRef,
  currentSrc,
  isSplitView,
  FOV,
  cameraPositions,
  loadedImageIndex,
  debugCameraPositions,
  transformCameraPosition,
  activeObjectId,
  children,
  isSecondScene = false,
  imageWidth,
  imageHeight,
  onSelectObject,
}: PanoramaSceneProps) => {
  const sceneStyle: React.CSSProperties = isSecondScene
    ? {
        width: "50%",
        height: "100%",
        float: "right",
        boxSizing: "border-box",
      }
    : {
        width: isSplitView ? "50%" : "100%",
        height: "100%",
        float: isSplitView ? "left" : "none",
        boxSizing: "border-box",
      };

  return (
    <a-scene
      ref={sceneRef}
      embedded
      style={sceneStyle}
      cursor="rayOrigin: mouse"
      raycaster="objects: .clickable"
      xr-mode-ui="enabled: false"
      background="color: #000"
    >
      <a-assets>
        <img
          alt="doneIcon"
          id="doneIcon"
          src={doneIcon}
          crossOrigin="anonymous"
        />
        <img
          alt="cancelIcon"
          id="cancelIcon"
          src={cancelIcon}
          crossOrigin="anonymous"
        />
      </a-assets>

      {/* 360° Image */}
      <a-sky
        src={currentSrc}
        material="shader: flat; side: back"
        geometry="radius: 500"
        rotation="0 -90 0"
        ref={skyRef}
      />

      {/* Debug coordinate system - show X, Y, Z axes */}
      {debugCameraPositions && (
        <>
          {/* X-axis (red) */}
          <a-entity
            geometry="primitive: cylinder; radius: 0.02; height: 2"
            material="color: red"
            position="1 -1 0"
            rotation="0 0 90"
          />
          <a-text value="X" position="2 -1 0" color="red" align="center" />

          {/* Y-axis (green) */}
          <a-entity
            geometry="primitive: cylinder; radius: 0.02; height: 2"
            material="color: green"
            position="0 0 0"
            rotation="0 0 0"
          />
          <a-text value="Y" position="0 1 0" color="green" align="center" />

          {/* Z-axis (blue) */}
          <a-entity
            geometry="primitive: cylinder; radius: 0.02; height: 2"
            material="color: blue"
            position="0 -1 -1"
            rotation="90 0 0"
          />
          <a-text value="Z" position="0 -1 -2" color="blue" align="center" />
        </>
      )}

      {/* Object overlays */}
      <ObjectOverlays
        loadedImageIndex={loadedImageIndex}
        cameraPositions={cameraPositions}
        activeObjectId={activeObjectId}
        cameraRef={cameraRef}
        imageWidth={imageWidth}
        imageHeight={imageHeight}
        onSelectObject={onSelectObject}
      />

      {children}

      {/* Camera positions */}
      <CameraPositions
        cameraPositions={cameraPositions}
        loadedImageIndex={loadedImageIndex}
        debugCameraPositions={debugCameraPositions}
        transformCameraPosition={transformCameraPosition}
      />

      {/* Debug info overlay */}
      {debugCameraPositions && (
        <a-text
          value="Debug Mode: Showing -2 to +3 frames around current\nWhite circles=Nearby frames\nClick white circles to navigate"
          position="0 2 -3"
          color="white"
          align="center"
        />
      )}

      {/* Camera with look controls for mouse/touch interaction */}
      <a-camera
        look-controls="enabled: false"
        custom-look-controls="enabled: true; pointerLockEnabled: false; reverseMouseDrag: false"
        wasd-controls="enabled: false"
        position="0 0 0"
        fov={FOV}
        id={isSecondScene ? "secondCamera" : "camera"}
        ref={cameraRef}
      />

      {/* Brighter lighting for better visibility */}
      <a-light type="ambient" color="#ffffff" intensity="1.2" />
      <a-light
        type="directional"
        position="0 1 0"
        color="#ffffff"
        intensity="0.3"
      />
    </a-scene>
  );
};

export default PanoramaScene;
